import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  Paper,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Zoom,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  User,
  MessageSquare,
  Calendar,
  Home,
  Building2,
  Hotel,
  ArrowRight,
  CheckCircle,
  Star,
  Award,
  Users,
  Lightbulb,
  Heart,
  Target
} from 'lucide-react';

// Contact Info Card Component
const ContactInfoCard = ({ icon: Icon, title, info, delay, link }) => {
  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%', 
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          cursor: link ? 'pointer' : 'default',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            borderColor: '#FF5722'
          }
        }}
        onClick={() => link && window.open(link, '_blank')}
      >
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              mx: 'auto',
              color: 'white',
              boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
            }}
          >
            <Icon size={36} />
          </Box>
          
          <Typography 
            variant="h5" 
            component="h3" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="body1" 
            color="#666"
            sx={{ 
              lineHeight: 1.6,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {info}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
};

// Feature Icon Component
const FeatureIcon = ({ icon: Icon, delay }) => {
  return (
    <Zoom in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: 0,
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
          animation: `iconFloat 3s ease-in-out ${delay * 0.1}s infinite alternate`,
          '@keyframes iconFloat': {
            '0%': { transform: 'translateY(0)' },
            '100%': { transform: 'translateY(-8px)' }
          }
        }}
      >
        <Icon size={28} />
      </Box>
    </Zoom>
  );
};

const ContactPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showElements, setShowElements] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: ''
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowElements(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setOpenSnackbar(true);
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      projectType: '',
      budget: '',
      timeline: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Call Us',
      info: '+91 40 6678 9000',
      link: 'tel:+914066789000'
    },
    {
      icon: Mail,
      title: 'Email Us',
      info: 'info@vectospacedesign.com',
      link: 'mailto:info@vectospacedesign.com'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      info: 'Galaxy Business Park, Level 4, Hi-Tech City Road, Madhapur, Hyderabad',
      link: 'https://maps.google.com'
    },
    {
      icon: Clock,
      title: 'Working Hours',
      info: 'Mon - Fri: 9:00 AM - 6:00 PM, Sat: 10:00 AM - 2:00 PM'
    }
  ];

  const featureIcons = [
    CheckCircle, Star, Award, Users, Lightbulb, Heart, Target, Home
  ];

  return (
    <Box sx={{ overflow: 'hidden', background: '#FAFAFA' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)',
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        {/* Background Geometric Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,87,34,0.03), transparent)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            background: 'linear-gradient(45deg, rgba(255,87,34,0.02), transparent)',
            transform: 'rotate(45deg)',
            zIndex: 0
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={showElements} timeout={1000}>
                <Box>
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 3, 
                      py: 1, 
                      background: 'rgba(255,87,34,0.08)',
                      border: '1px solid rgba(255,87,34,0.2)',
                      borderRadius: 0,
                      mb: 4
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#FF5722"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: 2,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Get in Touch
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="#1A1A1A"
                    sx={{ 
                      fontSize: { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
                      fontWeight: 800,
                      lineHeight: 0.9,
                      mb: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Let's Create Your{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Dream
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      Space Together
                    </Box>
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="#666"
                    sx={{ 
                      maxWidth: 600, 
                      mb: 8,
                      lineHeight: 1.7,
                      fontSize: '1.2rem',
                      position: 'relative',
                      pl: 4,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        width: 3,
                        height: 40,
                        background: 'linear-gradient(to bottom, #FF5722, #FF8A65)'
                      }
                    }}
                  >
                    Ready to transform your space? Our expert design team is here to bring your vision to life. 
                    Get in touch for a free consultation and let's start your design journey.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 6 }}>
                    <Button 
                      href="#contact-form"
                      variant="contained" 
                      size="large"
                      endIcon={<ArrowRight />}
                      sx={{
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(255,87,34,0.4)'
                        }
                      }}
                    >
                      Start Your Project
                    </Button>
                    
                    <Button 
                      href="tel:+914066789000"
                      variant="outlined"
                      size="large"
                      startIcon={<Phone />}
                      sx={{
                        borderColor: '#1A1A1A',
                        color: '#1A1A1A',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 1,
                        borderWidth: 2,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#FF5722',
                          color: '#FF5722',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(255,87,34,0.2)'
                        }
                      }}
                    >
                      Call Now
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Slide direction="left" in={showElements} timeout={1000}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 400,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {/* Feature Icons Grid */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 3,
                      width: '100%',
                      maxWidth: 320,
                      zIndex: 2
                    }}
                  >
                    {featureIcons.map((IconComponent, index) => (
                      <FeatureIcon key={index} icon={IconComponent} delay={index * 100} />
                    ))}
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Contact Information Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          background: '#FFFFFF',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Fade in={showElements}>
              <Box>
                <Typography
                  variant="overline"
                  color="#999"
                  sx={{
                    fontWeight: 500,
                    letterSpacing: 3,
                    mb: 2,
                    display: 'block',
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Contact Information
                </Typography>
                <Typography
                  variant="h2"
                  component="h2"
                  color="#1A1A1A"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Get in Touch with Us
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  color="#666"
                  sx={{
                    maxWidth: 700,
                    mx: 'auto',
                    fontWeight: 400,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                  }}
                >
                  Multiple ways to reach our team. Choose what works best for you.
                </Typography>
              </Box>
            </Fade>
          </Box>
          
          <Grid container spacing={4}>
            {contactInfo.map((info, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <ContactInfoCard {...info} delay={300 * (index + 1)} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box
        id="contact-form"
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <Fade in={showElements}>
                <Box>
                  <Typography
                    variant="overline"
                    color="#999"
                    sx={{
                      fontWeight: 500,
                      letterSpacing: 3,
                      mb: 2,
                      display: 'block',
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Contact Form
                  </Typography>
                  <Typography
                    variant="h2"
                    component="h2"
                    color="#1A1A1A"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Start Your Project
                  </Typography>
                  <Typography
                    variant="h6"
                    component="p"
                    color="#666"
                    sx={{
                      mb: 4,
                      fontWeight: 400,
                      lineHeight: 1.6,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    Fill out the form below and our team will get back to you within 24 hours 
                    to discuss your project requirements and schedule a consultation.
                  </Typography>

                  <Paper
                    sx={{
                      p: 4,
                      background: '#FFFFFF',
                      border: '1px solid #E0E0E0',
                      borderRadius: 0,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Box component="form" onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            variant="outlined"
                            InputProps={{
                              startAdornment: <User size={20} style={{ marginRight: 8, color: '#FF5722' }} />,
                              sx: {
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#E0E0E0'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                }
                              }
                            }}
                            InputLabelProps={{
                              sx: { 
                                color: '#666',
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            variant="outlined"
                            InputProps={{
                              startAdornment: <Mail size={20} style={{ marginRight: 8, color: '#FF5722' }} />,
                              sx: {
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#E0E0E0'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                }
                              }
                            }}
                            InputLabelProps={{
                              sx: { 
                                color: '#666',
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            variant="outlined"
                            InputProps={{
                              startAdornment: <Phone size={20} style={{ marginRight: 8, color: '#FF5722' }} />,
                              sx: {
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#E0E0E0'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                }
                              }
                            }}
                            InputLabelProps={{
                              sx: { 
                                color: '#666',
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel 
                              sx={{ 
                                color: '#666',
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                              }}
                            >
                              Project Type
                            </InputLabel>
                            <Select
                              name="projectType"
                              value={formData.projectType}
                              onChange={handleInputChange}
                              label="Project Type"
                              sx={{
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#E0E0E0'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                }
                              }}
                            >
                              <MenuItem value="residential">Residential Design</MenuItem>
                              <MenuItem value="commercial">Commercial Design</MenuItem>
                              <MenuItem value="hospitality">Hospitality Design</MenuItem>
                              <MenuItem value="renovation">Renovation</MenuItem>
                              <MenuItem value="consultation">Consultation Only</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel 
                              sx={{ 
                                color: '#666',
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                              }}
                            >
                              Budget Range
                            </InputLabel>
                            <Select
                              name="budget"
                              value={formData.budget}
                              onChange={handleInputChange}
                              label="Budget Range"
                              sx={{
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#E0E0E0'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                }
                              }}
                            >
                              <MenuItem value="under-5">Under ₹5 Lakhs</MenuItem>
                              <MenuItem value="5-10">₹5 - ₹10 Lakhs</MenuItem>
                              <MenuItem value="10-25">₹10 - ₹25 Lakhs</MenuItem>
                              <MenuItem value="25-50">₹25 - ₹50 Lakhs</MenuItem>
                              <MenuItem value="above-50">Above ₹50 Lakhs</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel 
                              sx={{ 
                                color: '#666',
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                              }}
                            >
                              Timeline
                            </InputLabel>
                            <Select
                              name="timeline"
                              value={formData.timeline}
                              onChange={handleInputChange}
                              label="Timeline"
                              sx={{
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#E0E0E0'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                }
                              }}
                            >
                              <MenuItem value="immediate">Immediate (Within 1 month)</MenuItem>
                              <MenuItem value="3-months">1-3 months</MenuItem>
                              <MenuItem value="6-months">3-6 months</MenuItem>
                              <MenuItem value="planning">Just planning</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Project Details"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            multiline
                            rows={4}
                            variant="outlined"
                            placeholder="Tell us about your project, requirements, and vision..."
                            InputProps={{
                              startAdornment: <MessageSquare size={20} style={{ marginRight: 8, color: '#FF5722', alignSelf: 'flex-start', marginTop: 8 }} />,
                              sx: {
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#E0E0E0'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#FF5722'
                                }
                              }
                            }}
                            InputLabelProps={{
                              sx: { 
                                color: '#666',
                                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            fullWidth
                            endIcon={<Send />}
                            sx={{
                              py: 1.5,
                              borderRadius: 0,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 1,
                              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                              boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 35px rgba(255,87,34,0.4)'
                              }
                            }}
                          >
                            Send Message
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Slide direction="right" in={showElements} timeout={1000}>
                <Box>
                  <Paper
                    sx={{
                      p: 4,
                      background: '#FFFFFF',
                      border: '1px solid #E0E0E0',
                      borderRadius: 0,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Typography 
                      variant="h4" 
                      color="#1A1A1A" 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 600,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      Why Choose VectoSpaceDesign?
                    </Typography>
                    
                    <Box sx={{ my: 4 }}>
                      {[
                        { icon: CheckCircle, text: 'Free initial consultation and quote' },
                        { icon: Star, text: '6+ years of design excellence' },
                        { icon: Award, text: 'Award-winning design team' },
                        { icon: Users, text: 'Dedicated project manager for each client' },
                        { icon: Lightbulb, text: 'Innovative and sustainable solutions' },
                        { icon: Target, text: 'On-time and on-budget delivery' }
                      ].map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 0,
                              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 3,
                              boxShadow: '0 4px 15px rgba(255,87,34,0.3)'
                            }}
                          >
                            <item.icon size={20} color="white" />
                          </Box>
                          <Typography 
                            variant="body1" 
                            color="#666"
                            sx={{
                              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                            }}
                          >
                            {item.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    
                    <Typography 
                      variant="body1" 
                      color="#666" 
                      sx={{ 
                        fontStyle: 'italic',
                        lineHeight: 1.6,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      "Ready to transform your space? Let's start the conversation and make your design dreams a reality."
                    </Typography>
                  </Paper>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity="success" 
          sx={{ 
            background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
            color: 'white',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
            '& .MuiAlert-icon': { color: 'white' }
          }}
        >
          Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;